import {
  PerformersCard as BasePerformersCard,
  PerformersCardProps as BasePerformersCardProps,
} from '@features/campaigns/components/RealTimeOptimization/Performers/PerformersCard';
import { AIInsights } from '@features/ai-insights';
import { RslDimension, RslReport } from '../../api/schema';

type LiftOption = BasePerformersCardProps<
  PerformersCardProps['reports'][number],
  PerformersCardProps['dimensions'][number],
  'visit' | 'sales'
>['liftOptions'][number];

const salesLiftOption = {
  value: 'sales',
  name: 'Sales',
  chartLabel: 'Incremental Sales Lift',
} as const satisfies LiftOption;

const visitLiftOption = {
  value: 'visit',
  name: 'Visit',
  chartLabel: 'Incremental Visit Lift',
} as const satisfies LiftOption;

const buildLiftOptions = (
  hasVisitMetrics: boolean
): BasePerformersCardProps<
  PerformersCardProps['reports'][number],
  PerformersCardProps['dimensions'][number],
  'visit' | 'sales'
>['liftOptions'] => [
  salesLiftOption,
  ...(hasVisitMetrics ? [visitLiftOption] : []),
];

type MinimalRslDimension = Pick<
  RslDimension,
  | 'dimensionName'
  | 'dimensionValue'
  | 'incrementalSalesLift'
  | 'incrementalVisitLift'
  | 'statisticallySignificant'
>;
type MinimalRslReport = Pick<
  RslReport,
  | 'reportDate'
  | 'incrementalSalesLift'
  | 'incrementalVisitLift'
  | 'hasVisitMetrics'
> & { dimensions: MinimalRslDimension[] };

export type PerformersCardProps = {
  reports: MinimalRslReport[];
  dimensions: MinimalRslDimension[];
  aiInsights: AIInsights | null;
};

export const PerformersCard = (props: PerformersCardProps) => {
  const hasVisitMetrics = props.reports.some(report => report.hasVisitMetrics);

  return (
    <BasePerformersCard
      reports={props.reports}
      reportDates={props.reports.map(report => report.reportDate)}
      currentSubdimensions={props.dimensions.map(d => ({
        ...d,
        statisticallySignificant: d.statisticallySignificant,
        value: d.dimensionValue,
        name: d.dimensionName,
      }))}
      getValueByLiftType={{
        sales: subdimension => subdimension.incrementalSalesLift,
        visit: subdimension => subdimension.incrementalVisitLift ?? null,
      }}
      getOverallValueByLiftType={{
        sales: report => report.incrementalSalesLift,
        visit: report => report.incrementalVisitLift ?? null,
      }}
      getSubdimensionsFromReport={report =>
        report.dimensions.map(d => ({
          ...d,
          statisticallySignificant: d.statisticallySignificant,
          value: d.dimensionValue,
          name: d.dimensionName,
        }))
      }
      defaultLiftType="sales"
      liftOptions={buildLiftOptions(hasVisitMetrics)}
      aiInsights={props.aiInsights}
    />
  );
};
